/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: List of all historical versions for a component with comparison selection
 * Style referenceFiles:
 * Design for: Desktop
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - View timeline of versions (major, minor, patch)
 * - Select 2 versions to compare
 * - Publish new version
 * - Navigate to: ComponentDetail, CompareView (modal/split)
 * </page-design>
 */

import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { LayoutSidebar } from '@/components/function/LayoutSidebar'
import * as VersionHistoryStore from '@/stores/VersionHistoryStore'
import { pageLinks } from '@/pageLinks'

function PublishModal() {
  const setShow = useSetAtom(VersionHistoryStore.showPublishModalAtom)
  const publish = useSetAtom(VersionHistoryStore.publishAtom)
  const [version, setVersion] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async () => {
    setSubmitting(true)
    try { await publish({ version, changelogNotes: notes }) } finally { setSubmitting(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShow(false)}>
      <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.1)] w-[480px] p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-6">发布新版本</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">版本号 *</label>
            <input type="text" value={version} onChange={e => setVersion(e.target.value)} placeholder="例如：1.3.0" className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600 mb-1.5 block">变更说明</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="描述本次版本的变更内容..." rows={4} className="w-full px-4 py-3 bg-gray-50 rounded-xl text-sm outline-none focus:ring-1 focus:ring-[#1D263A]/15 resize-none" />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={() => setShow(false)} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 transition-all">取消</button>
          <button onClick={handleSubmit} disabled={submitting || !version.trim()} className="bg-[#1D263A] hover:bg-[#151D2E] disabled:opacity-50 text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm">{submitting ? '发布中...' : '发布版本'}</button>
        </div>
      </div>
    </div>
  )
}

function VersionList() {
  const { data } = useAtomValue(VersionHistoryStore.refreshAtom)
  const selected = useAtomValue(VersionHistoryStore.selectedVersionsAtom)
  const toggle = useSetAtom(VersionHistoryStore.toggleVersionAtom)

  if (!data) return null

  return (
    <div className="space-y-4">
      {data.versions.map(v => (
        <div key={v.id} className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all cursor-pointer ${selected.includes(v.id) ? 'border-[#1D263A]/15 bg-[#1D263A]/5' : 'border-transparent bg-white shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)]'}`} onClick={() => toggle(v.id)}>
          <div className={`w-5 h-5 mt-0.5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${selected.includes(v.id) ? 'bg-[#1D263A] border-[#1D263A]' : 'border-gray-300'}`}>
            {selected.includes(v.id) && <Icon icon="lucide:check" className="text-white text-sm" />}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-base font-bold font-mono">v{v.version}</span>
              <span className="text-xs text-gray-400">{v.createdAt ? new Date(v.createdAt).toLocaleDateString('zh-CN') : ''}</span>
              {v.publishedBy && <span className="text-xs text-gray-500">by {v.publishedBy}</span>}
            </div>
            {v.changelogNotes && <p className="text-sm text-gray-600 leading-relaxed">{v.changelogNotes}</p>}
          </div>
        </div>
      ))}
      {data.versions.length === 0 && <div className="text-center py-16 text-gray-400">暂无版本记录</div>}
    </div>
  )
}

export default function VersionHistory() {
  const { data, isPending } = useAtomValue(VersionHistoryStore.refreshAtom)
  const selected = useAtomValue(VersionHistoryStore.selectedVersionsAtom)
  const showPublish = useAtomValue(VersionHistoryStore.showPublishModalAtom)
  const setShowPublish = useSetAtom(VersionHistoryStore.showPublishModalAtom)
  const showCompare = useAtomValue(VersionHistoryStore.showCompareAtom)
  const setShowCompare = useSetAtom(VersionHistoryStore.showCompareAtom)

  return (
    <div className="flex h-screen w-full bg-[#F8F9FA] font-['Noto_Sans_SC',sans-serif] text-[#1A1D25] overflow-hidden">
      <LayoutSidebar />
      <div className="flex-1 flex flex-col h-full overflow-hidden p-6 pb-0 relative">
        <header className="flex items-center justify-between mb-6 px-2">
          <div className="flex items-center gap-3">
            {data && <Link to={pageLinks.ComponentDetail(data.component.id)} className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-600 shadow-[0_2px_10px_rgb(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgb(0,0,0,0.06)] transition-all"><Icon icon="lucide:arrow-left" className="text-xl" /></Link>}
            <div>
              <h1 className="text-2xl font-bold tracking-tight">版本历史</h1>
              {data && <p className="text-sm text-gray-500">{data.component.name} · 当前 v{data.component.currentVersion}</p>}
            </div>
          </div>
          <button onClick={() => setShowPublish(true)} className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2.5 px-5 rounded-xl shadow-[0_4px_12px_rgba(29,38,58,0.25)] transition-all text-sm flex items-center gap-2"><Icon icon="lucide:upload" />发布新版本</button>
        </header>

        <div className="flex-1 overflow-y-auto pb-24 px-2">
          {isPending ? <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white rounded-2xl" />)}</div> : <VersionList />}
        </div>

        {selected.length === 2 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.1)] px-6 py-4 flex items-center gap-4">
            <span className="text-sm font-medium">已选择 2 个版本</span>
            <button onClick={() => setShowCompare(true)} className="bg-[#1D263A] hover:bg-[#151D2E] text-white font-medium py-2 px-5 rounded-xl text-sm transition-all">对比版本</button>
          </div>
        )}
      </div>

      {showPublish && <PublishModal />}

      {showCompare && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setShowCompare(false)}>
          <div className="bg-white rounded-[24px] shadow-[0_8px_30px_rgb(0,0,0,0.1)] w-[800px] max-h-[80vh] p-6 overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">版本对比</h2>
              <button onClick={() => setShowCompare(false)} className="text-gray-400 hover:text-gray-600"><Icon icon="lucide:x" className="text-xl" /></button>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {selected.map(id => {
                const v = data?.versions.find(ver => ver.id === id)
                return v ? (
                  <div key={id} className="bg-gray-50 rounded-xl p-4">
                    <h3 className="font-bold font-mono mb-2">v{v.version}</h3>
                    <p className="text-sm text-gray-600">{v.changelogNotes || '无变更说明'}</p>
                    <p className="text-xs text-gray-400 mt-2">{v.createdAt ? new Date(v.createdAt).toLocaleDateString('zh-CN') : ''}</p>
                  </div>
                ) : null
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
